// src/pages/AnnouncementsPage.js
//
// /announcements (UI revamp; admins). Three tabs, so none of them scrolls far:
//   - Post: title, message, importance, who sees it, optional show-from /
//     show-until. "Who sees it" is multi-select (2026-11-02): everyone at all
//     companies (owners), or per company "everyone at <company>" (company
//     admins) or some of its sites (any admin of those sites). Each audience
//     becomes its own post, linked by a groupId, shown and removed as one.
//     Posted as the signed-in person's login email; nothing to type.
//   - Showing now: what they manage that's live, and separately what's
//     scheduled, each with Remove (asks first; stamps removedAt, keeps the record).
//   - History: everything they manage that was removed or has ended, with who
//     posted / removed it and when. Read-only; nothing is ever deleted.
// Who may post where is enforced by firestore.rules, post by post.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { listSites } from "../services/membershipService";
import {
  LIMITS,
  audienceSummary,
  audienceText,
  canPostToEveryone,
  emptyAudience,
  forAdmin,
  forHistory,
  groupPosts,
  historyText,
  isScheduled,
  postableCompanies,
  postableSites,
  postedText,
  statusText,
  toMs,
  toggleAudience,
  validatePost,
} from "../services/announcements";
import { loadManageable, postAnnouncement, removeAnnouncement } from "../services/announcementService";

const EMPTY = { title: "", message: "", importance: "normal", showFrom: "", showUntil: "" };

const TABS = [
  { id: "post", label: "Post" },
  { id: "showing", label: "Showing now" },
  { id: "history", label: "History" },
];

const FieldError = ({ id, text }) => (text ? <p id={id} className="rx-home-warn text-xs mt-1">{text}</p> : null);

/** One announcement (or a group posted together): title, audience, status tag, message, who posted it. */
const AnnCard = ({ post, meta, tag, footer, action, variant = "" }) => (
  <li className={`rx-ann-item ${post.importance === "important" ? "rx-ann-item--important" : ""} ${variant}`}>
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <h3 className="text-sm font-semibold break-words">{post.title}</h3>
          {tag}
          {post.importance === "important" && <span className="rx-ann-tag rx-ann-tag--important">Important</span>}
        </div>
        <p className="rx-home-muted text-xs mt-0.5">{meta}</p>
      </div>
      {action}
    </div>
    <p className="text-sm mt-1 whitespace-pre-line break-words">{post.message}</p>
    {footer && <p className="rx-home-muted text-[11px] mt-1.5 break-all">{footer}</p>}
  </li>
);

export default function AnnouncementsPage() {
  const navigate = useNavigate();
  const { user, access, tenantId, siteId, tenantsMeta = [], sitesMeta = [] } = useTenant();

  // Who sees it: the companies and sites this person can post to.
  const companies = useMemo(() => postableCompanies(access, tenantsMeta), [access, tenantsMeta]);
  const [sitesByCompany, setSitesByCompany] = useState({});
  useEffect(() => {
    let cancelled = false;
    companies.forEach((c) => {
      const load = c.id === tenantId && sitesMeta.length ? Promise.resolve(sitesMeta) : listSites(c.id);
      load
        .then((sites) => !cancelled && setSitesByCompany((prev) => ({ ...prev, [c.id]: postableSites(access, c.id, sites) })))
        .catch((e) => {
          console.error("Couldn't load sites for", c.id, e);
          if (!cancelled) setSitesByCompany((prev) => ({ ...prev, [c.id]: [] }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [companies, access, tenantId, sitesMeta]);
  // Names for every site we know about (for "Dubois, Ohio only").
  const allSites = useMemo(
    () => Object.entries(sitesByCompany).flatMap(([t, list]) => list.map((s) => ({ id: s.id, name: s.label, tenantId: t }))),
    [sitesByCompany]
  );
  const meta = { tenantsMeta, sitesMeta: [...allSites, ...sitesMeta.map((s) => ({ ...s, tenantId }))] };
  const companyLabel = (id) => companies.find((c) => c.id === id)?.label || id;
  const siteLabel = (t, s) => (sitesByCompany[t] || []).find((x) => x.id === s)?.label || s;
  const canPostAnywhere = canPostToEveryone(access) || companies.length > 0;

  const [tab, setTab] = useState("post");
  const [form, setForm] = useState(EMPTY);
  const [audience, setAudience] = useState(emptyAudience);
  const [errors, setErrors] = useState({});
  const [posting, setPosting] = useState(false);
  const [notice, setNotice] = useState("");
  const [postError, setPostError] = useState("");
  const [list, setList] = useState({ loading: true, all: [], error: "" });
  const [confirm, setConfirm] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Start with the site you're on ticked, when you can post to it.
  useEffect(() => {
    const here = sitesByCompany[tenantId] || [];
    if (here.some((s) => s.id === siteId)) {
      setAudience((a) => (a.everyone || Object.keys(a.companies).length ? a : toggleAudience(a, { kind: "site", companyId: tenantId, siteId })));
    }
  }, [sitesByCompany, tenantId, siteId]);

  const adminSiteIds = useMemo(() => (sitesByCompany[tenantId] || []).map((s) => s.id), [sitesByCompany, tenantId]);
  const reload = useCallback(async () => {
    setList((l) => ({ ...l, loading: true }));
    try {
      const all = await loadManageable({ access, tenantId, adminSiteIds });
      setList({ loading: false, all, error: "" });
    } catch (e) {
      console.error("Couldn't load announcements:", e);
      setList({ loading: false, all: [], error: "Couldn't load announcements right now." });
    }
  }, [access, tenantId, adminSiteIds]);
  useEffect(() => {
    reload();
  }, [reload]);

  const now = Date.now();
  const currentGroups = useMemo(() => groupPosts(forAdmin(list.all, access, now)), [list.all, access, now]);
  const showingGroups = currentGroups.filter((g) => !isScheduled(g.posts[0], now));
  const scheduledGroups = currentGroups
    .filter((g) => isScheduled(g.posts[0], now))
    .sort((a, b) => toMs(a.posts[0].showFrom) - toMs(b.posts[0].showFrom));
  const historyGroups = useMemo(() => groupPosts(forHistory(list.all, access, now)), [list.all, access, now]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (choice) => setAudience((a) => toggleAudience(a, choice));

  const submit = async (e) => {
    e.preventDefault();
    setNotice("");
    setPostError("");
    const r = validatePost({ ...form, audience }, access);
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    setPosting(true);
    try {
      await postAnnouncement({ data: r.data, targets: r.targets, user });
      setForm(EMPTY);
      setNotice(r.data.showFrom && r.data.showFrom > new Date() ? "Scheduled. You'll find it under Showing now." : "Posted. It's showing now.");
      reload();
    } catch (err) {
      console.error("Couldn't post the announcement:", err);
      setPostError("Couldn't post it. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const doRemove = async () => {
    if (!confirm) return;
    setRemoving(true);
    try {
      await removeAnnouncement({ posts: confirm.posts, user });
      setConfirm(null);
      reload();
    } catch (err) {
      console.error("Couldn't remove the announcement:", err);
      setPostError("Couldn't remove it. Please try again.");
      setConfirm(null);
    } finally {
      setRemoving(false);
    }
  };

  const removeButton = (g) => (
    <button type="button" className="rx-home-link shrink-0" onClick={() => setConfirm(g)}>
      Remove
    </button>
  );

  const counts = { showing: currentGroups.length, history: historyGroups.length };
  const summary = audienceSummary(audience, { companyLabel, siteLabel });

  return (
    <div className="rx-home min-h-screen px-4 sm:px-8 py-8">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="rx-home-link inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="rx-home-greeting" style={{ fontSize: "1.6rem" }}>
            Announcements
          </h1>
        </div>

        <div role="tablist" aria-label="Announcements" className="rx-home-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`ann-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`ann-panel-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rx-home-tab ${tab === t.id ? "rx-home-tab--on" : ""}`}
            >
              {t.label}
              {!list.loading && counts[t.id] > 0 && <span className="rx-home-tab-count">{counts[t.id]}</span>}
            </button>
          ))}
        </div>

        {tab === "post" && (
          <section role="tabpanel" id="ann-panel-post" aria-label="Post" className="rx-home-card">
            <h2 className="rx-home-title mb-3">Post an announcement</h2>
            {!canPostAnywhere ? (
              <p className="rx-home-muted text-sm">You don't manage any audience, so you can't post announcements.</p>
            ) : (
              <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="ann-title" className="text-sm font-medium block mb-1">
                    Title
                  </label>
                  <input
                    id="ann-title"
                    value={form.title}
                    onChange={set("title")}
                    maxLength={LIMITS.title}
                    aria-invalid={!!errors.title}
                    aria-describedby="ann-title-err"
                    className="rx-home-input w-full"
                  />
                  <div className="flex justify-between">
                    <FieldError id="ann-title-err" text={errors.title} />
                    <span className="rx-home-muted text-[11px] ml-auto tabular-nums">
                      {form.title.length} / {LIMITS.title}
                    </span>
                  </div>
                </div>
                <div>
                  <label htmlFor="ann-message" className="text-sm font-medium block mb-1">
                    Message
                  </label>
                  <textarea
                    id="ann-message"
                    value={form.message}
                    onChange={set("message")}
                    maxLength={LIMITS.message}
                    rows={5}
                    aria-invalid={!!errors.message}
                    aria-describedby="ann-message-err"
                    className="rx-home-input w-full resize-y"
                  />
                  <div className="flex justify-between">
                    <FieldError id="ann-message-err" text={errors.message} />
                    <span className="rx-home-muted text-[11px] ml-auto tabular-nums">
                      {form.message.length} / {LIMITS.message}
                    </span>
                  </div>
                </div>
                <fieldset>
                  <legend className="text-sm font-medium mb-1">Importance</legend>
                  <div className="flex gap-5">
                    {[
                      ["normal", "Normal"],
                      ["important", "Important"],
                    ].map(([v, label]) => (
                      <label key={v} className="inline-flex items-center gap-2 text-sm">
                        <input type="radio" name="ann-importance" value={v} checked={form.importance === v} onChange={set("importance")} />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset aria-describedby="ann-audience-err">
                  <legend className="text-sm font-medium mb-1">Who sees it</legend>
                  <p className="rx-home-muted text-xs mb-2">Pick as many as you like.</p>
                  {canPostToEveryone(access) && (
                    <label className="rx-aud-row rx-aud-row--top">
                      <input type="checkbox" className="rx-home-check" checked={audience.everyone} onChange={() => toggle({ kind: "everyone" })} />
                      <span className="text-sm font-medium">Everyone at all companies</span>
                    </label>
                  )}
                  <div className="space-y-2 mt-2">
                    {companies.map((c) => {
                      const sel = audience.companies[c.id] || { whole: false, siteIds: [] };
                      const sites = sitesByCompany[c.id];
                      const covered = audience.everyone;
                      return (
                        <div key={c.id} className={`rx-aud-company ${covered ? "rx-aud-company--covered" : ""}`} role="group" aria-label={c.label}>
                          <p className="text-sm font-semibold mb-1">{c.label}</p>
                          {c.whole && (
                            <label className="rx-aud-row">
                              <input
                                type="checkbox"
                                className="rx-home-check"
                                checked={covered || sel.whole}
                                disabled={covered}
                                onChange={() => toggle({ kind: "company", companyId: c.id })}
                              />
                              <span className="text-sm">Everyone at {c.label}</span>
                            </label>
                          )}
                          <p className="rx-home-muted text-[11px] mt-1.5 mb-1">{c.whole ? "Or only these sites:" : "Sites:"}</p>
                          {!sites ? (
                            <p className="rx-home-muted text-xs">Loading sites…</p>
                          ) : sites.length === 0 ? (
                            <p className="rx-home-muted text-xs">No sites.</p>
                          ) : (
                            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                              {sites.map((s) => (
                                <label key={s.id} className="rx-aud-row">
                                  <input
                                    type="checkbox"
                                    className="rx-home-check"
                                    checked={covered || sel.whole || sel.siteIds.includes(s.id)}
                                    disabled={covered || sel.whole}
                                    onChange={() => toggle({ kind: "site", companyId: c.id, siteId: s.id })}
                                  />
                                  <span className="text-sm">{s.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <FieldError id="ann-audience-err" text={errors.audience} />
                  {summary && (
                    <p className="text-xs mt-2" aria-live="polite">
                      <span className="rx-home-muted">Will show to: </span>
                      {summary}
                    </p>
                  )}
                </fieldset>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ann-from" className="text-sm font-medium block mb-1">
                      Show from <span className="rx-home-muted font-normal">(optional)</span>
                    </label>
                    <input id="ann-from" type="date" value={form.showFrom} onChange={set("showFrom")} className="rx-home-input w-full" />
                    <FieldError id="ann-from-err" text={errors.showFrom} />
                  </div>
                  <div>
                    <label htmlFor="ann-until" className="text-sm font-medium block mb-1">
                      Show until <span className="rx-home-muted font-normal">(optional)</span>
                    </label>
                    <input id="ann-until" type="date" value={form.showUntil} onChange={set("showUntil")} className="rx-home-input w-full" />
                    <FieldError id="ann-until-err" text={errors.showUntil} />
                  </div>
                </div>
                <p className="rx-home-muted text-xs -mt-2">
                  With no end date, it shows until an admin removes it. People will see your login email
                  {user?.email ? ` (${user.email})` : ""} as who posted it.
                </p>
                {postError && <p className="rx-home-warn text-sm">{postError}</p>}
                {notice && (
                  <p className="text-sm" role="status">
                    {notice}
                  </p>
                )}
                <button type="submit" className="rx-home-button" disabled={posting}>
                  {posting ? "Posting…" : "Post"}
                </button>
              </form>
            )}
          </section>
        )}

        {tab === "showing" && (
          <section role="tabpanel" id="ann-panel-showing" aria-label="Showing now" className="rx-home-card space-y-5">
            {list.loading ? (
              <p className="rx-home-muted text-sm">Loading…</p>
            ) : list.error ? (
              <p className="rx-home-muted text-sm">{list.error}</p>
            ) : (
              <>
                {postError && <p className="rx-home-warn text-sm">{postError}</p>}
                <div role="group" aria-label="Showing now">
                  <h2 className="rx-home-title mb-2">Showing now</h2>
                  {showingGroups.length === 0 ? (
                    <p className="rx-home-muted text-sm">Nothing you manage is showing right now.</p>
                  ) : (
                    <ul className="space-y-2">
                      {showingGroups.map((g) => (
                        <AnnCard
                          key={g.key}
                          post={g.posts[0]}
                          tag={<span className="rx-ann-tag rx-ann-tag--live">Showing</span>}
                          meta={`${audienceText(g.posts, meta)} · ${statusText(g.posts[0], now)}`}
                          footer={postedText(g.posts[0])}
                          action={removeButton(g)}
                        />
                      ))}
                    </ul>
                  )}
                </div>
                <div role="group" aria-label="Scheduled">
                  <h2 className="rx-home-title mb-2">Scheduled</h2>
                  {scheduledGroups.length === 0 ? (
                    <p className="rx-home-muted text-sm">Nothing scheduled.</p>
                  ) : (
                    <ul className="space-y-2">
                      {scheduledGroups.map((g) => (
                        <AnnCard
                          key={g.key}
                          post={g.posts[0]}
                          variant="rx-ann-item--scheduled"
                          tag={<span className="rx-ann-tag rx-ann-tag--scheduled">{statusText(g.posts[0], now)}</span>}
                          meta={audienceText(g.posts, meta)}
                          footer={postedText(g.posts[0])}
                          action={removeButton(g)}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {tab === "history" && (
          <section role="tabpanel" id="ann-panel-history" aria-label="History" className="rx-home-card">
            <h2 className="rx-home-title mb-1">History</h2>
            <p className="rx-home-muted text-xs mb-3">Removed and ended announcements you manage. Records are never deleted.</p>
            {list.loading ? (
              <p className="rx-home-muted text-sm">Loading…</p>
            ) : list.error ? (
              <p className="rx-home-muted text-sm">{list.error}</p>
            ) : historyGroups.length === 0 ? (
              <p className="rx-home-muted text-sm">No removed or ended announcements yet.</p>
            ) : (
              <ul className="space-y-2">
                {historyGroups.map((g) => (
                  <AnnCard
                    key={g.key}
                    post={g.posts[0]}
                    variant="rx-ann-item--past"
                    tag={<span className="rx-ann-tag rx-ann-tag--past">{g.posts[0].removedAt ? "Removed" : "Ended"}</span>}
                    meta={audienceText(g.posts, meta)}
                    footer={`${postedText(g.posts[0])} · ${historyText(g.posts[0])}`}
                  />
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onMouseDown={() => !removing && setConfirm(null)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ann-confirm-title"
            className="rx-ann-confirm w-full max-w-md"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2 id="ann-confirm-title" className="rx-home-title mb-2">
              Remove “{confirm.posts[0].title}”?
            </h2>
            <p className="text-sm rx-home-muted">
              It stops showing for everyone it was posted to ({audienceText(confirm.posts, meta)}). The record is kept
              under History, with your email as who removed it.
            </p>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" className="rx-home-link" onClick={() => setConfirm(null)} disabled={removing}>
                Cancel
              </button>
              <button type="button" className="rx-home-button rx-ann-remove" onClick={doRemove} disabled={removing}>
                {removing ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
